import { apiClient, type Paginated } from '@/core/api';
import type { StaffInvoice, StaffInvoiceInput } from './types';

export const BASE = '/finance/staff-invoices/';

export interface StaffInvoiceListParams {
  page?: number;
  search?: string;
  employee?: number;
  status?: string;
  ordering?: string;
  /** Inclusive bounds (`YYYY-MM-DD`) on the issue date. */
  after?: string;
  before?: string;
}

export async function listStaffInvoices(
  params: StaffInvoiceListParams = {},
): Promise<Paginated<StaffInvoice>> {
  const { data } = await apiClient.get<Paginated<StaffInvoice>>(BASE, { params });
  return data;
}

/** Suggested next number for an employee (INV-<code>-<year>-NNN). */
export async function getNextStaffInvoiceNumber(employee: number): Promise<string> {
  const { data } = await apiClient.get<{ number: string }>(`${BASE}next-number/`, {
    params: { employee },
  });
  return data.number;
}

/** An employee as finance may see them — `{id, name, user}` only (DEV13-77);
 *  `/hrm/employees/` is HR-only, so the staff-invoice form uses this instead.
 *  `user` links the employee to their worklogs (null = no login). */
export interface StaffInvoiceEmployee {
  id: number;
  name: string;
  user: number | null;
}

export async function listStaffInvoiceEmployees(
  params: { search?: string; id?: number } = {},
): Promise<StaffInvoiceEmployee[]> {
  const { data } = await apiClient.get<{ results: StaffInvoiceEmployee[] }>(`${BASE}employees/`, {
    params,
  });
  return data.results;
}

/** One employee by id from the finance-owned list (null if not visible). */
export async function getStaffInvoiceEmployee(id: number): Promise<StaffInvoiceEmployee | null> {
  return (await listStaffInvoiceEmployees({ id }))[0] ?? null;
}

/** The employee's latest agreed salary and the hourly rate it implies (monthly ÷
 *  `standard_hours`, BE-ADR-049), for the form's rate prefill (DEV13-77) —
 *  `/hrm/salary-records/` is HR-only. `salary` is null when none is on file;
 *  its money fields (and effective_date) are null when masked for the caller. The rate is in the
 *  salary's `currency`, so the form takes that currency and FX with it. */
export interface StaffInvoiceSalary {
  employee: number;
  standard_hours: number;
  salary: {
    effective_date: string | null;
    amount: string | null;
    currency: string | null;
    fx_rate_to_base: string | null;
    /** Server-derived; null when the salary gives no positive rate (or masked). */
    hourly_rate: string | null;
  } | null;
}

/** The salary record effective on or before `periodEnd` (an ISO date; omitted
 *  → today) and the rate it implies (BE ADR-104). */
export async function getStaffInvoiceSalary(
  employee: number,
  periodEnd = '',
): Promise<StaffInvoiceSalary> {
  const { data } = await apiClient.get<StaffInvoiceSalary>(`${BASE}salary/`, {
    params: periodEnd ? { employee, period_end: periodEnd } : { employee },
  });
  return data;
}

export async function getStaffInvoice(id: string | number): Promise<StaffInvoice> {
  const { data } = await apiClient.get<StaffInvoice>(`${BASE}${id}/`);
  return data;
}

export async function createStaffInvoice(input: StaffInvoiceInput): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(BASE, input);
  return data;
}

export async function updateStaffInvoice(
  id: string | number,
  input: Partial<StaffInvoiceInput>,
): Promise<StaffInvoice> {
  const { data } = await apiClient.patch<StaffInvoice>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteStaffInvoice(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function issueStaffInvoice(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(`${BASE}${publicId}/issue/`);
  return data;
}

export async function generateStaffInvoicePdf(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(`${BASE}${publicId}/generate-pdf/`);
  return data;
}

export async function markStaffInvoicePaid(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(`${BASE}${publicId}/mark-paid/`);
  return data;
}

/** Finance signs an issued invoice on behalf of the employee (edge case). */
export async function signStaffInvoice(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(`${BASE}${publicId}/sign/`);
  return data;
}

/** Fetch the PDF as a blob (so JWT/org headers are attached, unlike a raw link). */
export async function getStaffInvoiceBlob(publicId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${BASE}${publicId}/download/`, {
    responseType: 'blob',
  });
  return data;
}

export async function downloadStaffInvoice(publicId: string, filename: string): Promise<void> {
  triggerBlobDownload(await getStaffInvoiceBlob(publicId), filename);
}

/**
 * Friendly download name, mirroring the backend `staff_invoice_filename`:
 * `Firstname-Lastname-Invoice-May-2026-ENG-001.pdf`. Spaces/punctuation collapse
 * to single dashes; month + year come from the invoice period.
 */
export function staffInvoiceFileName(
  inv: Pick<StaffInvoice, 'employee_name' | 'number' | 'public_id' | 'period_start'>,
): string {
  const monthYear = inv.period_start
    ? new Date(inv.period_start).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : '';
  const parts = [inv.employee_name || 'Employee', 'Invoice', monthYear, inv.number || inv.public_id];
  const safe = parts.filter(Boolean).join(' ').replace(/[^A-Za-z0-9_]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safe}.pdf`;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
